// Source: D:/AI/site-queensbutler/docs/structure/04_詳細設計_ページ内容スキーマ.md §4
import apprenticeFaq from '@/data/faq/apprentice.json'
import salonFaq from '@/data/faq/salon.json'
import shopFaq from '@/data/faq/shop.json'
import studioFaq from '@/data/faq/studio.json'
import type { FixedRouteData } from '@/data/types'
import type { FaqBlock, GalleryBuyBlock, PageContent } from '@/lib/contentTypes'
import { getRouteAncestors } from '@/lib/pageContent'
import { getImageMetadata } from '@/lib/images'

const siteUrl = 'https://queensbutler.jp'
const faqSources = { salon: salonFaq, studio: studioFaq, apprentice: apprenticeFaq, shop: shopFaq }
const concreteImageUrl = (src: string) => {
  const metadata = getImageMetadata(src)
  const fallback = metadata?.variants
    .filter((variant) => variant.format === 'jpg' || variant.format === 'png')
    .sort((left, right) => right.width - left.width)[0]?.src
  const resolved = fallback ?? src
  return resolved.startsWith('http') ? resolved : `${siteUrl}${resolved}`
}

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '一般社団法人全日本執事協会',
  brand: { '@type': 'Brand', name: "Queen's Butler" },
  url: siteUrl,
  address: {
    '@type': 'PostalAddress',
    addressRegion: '東京都',
    addressLocality: '江東区',
    streetAddress: '青海2-7-4',
    addressCountry: 'JP',
  },
}

export function breadcrumbSchema(route: FixedRouteData) {
  const entries = [
    { name: 'トップ', url: '/' },
    ...getRouteAncestors(route).map((item) => ({ name: item.name, url: item.url })),
    ...(route.url === '/' ? [] : [{ name: route.name, url: route.url }]),
  ]
  return {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: entries.map((item, index) => ({
      '@type': 'ListItem', position: index + 1, name: item.name, item: `${siteUrl}${item.url}`,
    })),
  }
}

function faqEntries(block: FaqBlock) {
  const entries = block.items ?? (block.ref
    ? faqSources[block.ref].items.slice(0, block.limit).map((item) => ({ q: item.question, a: item.answer }))
    : [])
  return block.limit === undefined ? entries : entries.slice(0, block.limit)
}

function faqSchema(blocks: FaqBlock[]) {
  const source = blocks.flatMap(faqEntries)
  return {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: source.map((item) => ({
      '@type': 'Question', name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  }
}

function serviceSchema(page: PageContent) {
  return {
    '@context': 'https://schema.org', '@type': 'Service',
    name: page.h1,
    description: page.definition,
    url: `${siteUrl}${page.url}`,
    provider: { '@id': `${siteUrl}/#organization`, name: '一般社団法人全日本執事協会' },
    areaServed: { '@type': 'Place', name: '東京都江東区青海' },
  }
}

function productSchema(page: PageContent, product: GalleryBuyBlock) {
  return {
    '@context': 'https://schema.org', '@type': 'Product',
    name: product.heading,
    description: product.description ?? page.definition,
    image: product.images.map((item) => concreteImageUrl(item.image)),
    brand: { '@type': 'Brand', name: "Queen's Butler" },
    offers: {
      '@type': 'Offer', priceCurrency: 'JPY', price: product.yen,
      ...(product.availability ? { availability: `https://schema.org/${product.availability}` } : {}),
      url: `${siteUrl}${page.url}`,
    },
  }
}

export function buildPageStructuredData(page: PageContent, route: FixedRouteData): object[] {
  const schemas: object[] = [{ ...organizationSchema, '@id': `${siteUrl}/#organization` }, breadcrumbSchema(route)]
  const faqBlocks = page.sections.filter((block): block is FaqBlock => block.type === 'faq')
  if (faqBlocks.length) schemas.push(faqSchema(faqBlocks))
  if (page.layout === 'service-top' || page.structuredData?.service) schemas.push(serviceSchema(page))
  const product = page.sections.find((block): block is GalleryBuyBlock => block.type === 'gallery-buy')
  if (product && (page.layout === 'shop-item' || page.structuredData?.product)) schemas.push(productSchema(page, product))
  return schemas
}
